import { LoginPage } from './pages/LoginPage';
import { Navigate, Route, Routes } from 'react-router-dom';
import { LayoutAdmin } from './components/templates/LayoutAdmin';
import { RequireAdminAuth } from './components/modules/AuthRouter';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPassword';
import { SettingPage } from './pages/SettingPage';
import { ForgotPasswordSuccessPage } from './pages/ForgotPasswordSuccessPage';
import { ResetPasswordSuccessPage } from './pages/ResetPasswordSuccessPage';
import { CreateClientPage } from './pages/Clients/create';
import { LocationPage } from './pages/LocationPage';
import { ClientsPage } from './pages/Clients';
import { CreateLocation } from './pages/LocationPage/create';
import { DocumentsPage } from './pages/DocumentsPage';
import { UserPage } from './pages/UserPage';
import Page403 from './pages/403';
import Page404 from './pages/404';
import { useAuth } from 'contexts/auth';
import { useFlash } from 'contexts/flash';
import { Role } from './pages/UserPage/type';
import { RoomPage } from './pages/RoomPage';
import { RoomReportViewPhotoPage } from './pages/RoomReportViewPhotoPage';
import { ReportPage } from './pages/ReportPage';
import { ValidationsPage } from './pages/ValidationsPage';
import { OptionsPage } from './pages/OptionsPage';
import { RoomLevelIssuesPage } from './pages/RoomLevelIssuesPage';
import { RoomReportCreateIssuePage } from './pages/RoomReportCreateIssuePage';
import { IssuesPage } from './pages/IssuesPage';
import { ActivityPage } from './pages/Activities';
import { BackgroundJobsPage } from './pages/BackgroundJobs';
import { RoomProvider } from 'contexts/RoomContext';
import { useEffect } from 'react';
import { requestForToken, onMessageListener } from 'services/webPushNotification';
import { NotificationsPage } from './pages/NotificationsPage';
import { FloorPlanViewPage } from './pages/FloorPlanViewPage';
import { FloorPlanLocationPage } from './pages/FloorPlan/components/modules/FloorPlanLocation';
import { FloorPlanPage } from './pages/FloorPlan';
import { UpdateFloorPlan } from './pages/FloorPlan/components/modules/UpdateFloorPlan/index';
import { UploadPlan } from './pages/FloorPlan/components/modules/UploadPlan';
import { UploadFloorPlan } from './pages/FloorPlan/components/modules/Create';
import { ViewFloorPlan } from './pages/FloorPlan/components/modules/ViewFloorPlan';
import { NotificationSettingsPage } from './pages/NotificationSettings';

export default function App() {
  const { auth } = useAuth();
  const { setFlash } = useFlash();

  useEffect(() => {
    if (auth) {
      const timer = setTimeout(() => {
        void requestForToken();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [auth]);

  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      // console.log('Foreground message received in App:', payload);
      const { title, body } = payload.notification || {};
      if (title && body) {
        setFlash({
          message: `${title}: ${body}`,
          type: 'info'
        });
      }
    });

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [setFlash]);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <RequireAdminAuth>
            <RoomProvider>
              <LayoutAdmin>
                <Routes>
                  <Route path="clients" element={<ClientsPage />} />
                  <Route path="activities" element={<ActivityPage />} />
                  <Route path="pdfmapping" element={<FloorPlanPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="/notification-settings" element={<NotificationSettingsPage />} />
                  <Route path="clients/create" element={<CreateClientPage />} />
                  <Route
                    path="clients/:clientName/:clientId/location/:locationId/room/issues/:issuesId/view-photo"
                    element={<RoomReportViewPhotoPage />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/:locationId/room/issues/create-issue"
                    element={<RoomReportCreateIssuePage />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/:locationId/room/issue-management/issues/:issuesId/view-photo"
                    element={<RoomReportViewPhotoPage />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/:locationId/room"
                    element={<RoomPage />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/:locationId/"
                    element={<LocationPage />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/:locationId/create"
                    element={<CreateLocation />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/"
                    element={<LocationPage />}
                  />
                  <Route
                    path="clients/:clientName/:clientId/location/create"
                    element={<CreateLocation />}
                  />
                  <Route
                    path="pdfmapping/uploadplan/:clientName/:clientId/location/:locationId/update"
                    element={<UpdateFloorPlan />}
                  />
                  <Route
                    path="/submit/:submitId/room-issues/:id"
                    element={<RoomLevelIssuesPage />}
                  />
                  <Route
                    path="pdfmapping/uploadplan/:clientName/:clientId/location/:locationId/"
                    element={<FloorPlanLocationPage />}
                  />
                  <Route
                    path="pdfmapping/uploadplan/:clientName/:clientId/location/"
                    element={<FloorPlanLocationPage />}
                  />
                  <Route path="pdfmapping/uploadplan" element={<UploadPlan />} />
                  <Route
                    path="pdfmapping/uploadplan/:clientName/:clientId/location/:locationId/upload"
                    element={<UploadFloorPlan />}
                  />
                  <Route
                    path="pdfmapping/uploadplan/:clientName/:clientId/location/:locationId/plans"
                    element={<ViewFloorPlan />}
                  />
                  <Route
                    path="pdfmapping/:clientName/:clientId/location/:locationId/view"
                    element={<FloorPlanViewPage />}
                  />
                  <Route path="issues" element={<IssuesPage />} />
                  <Route path="reports" element={<ReportPage />} />
                  <Route path="validation" element={<ValidationsPage />} />
                  <Route path="background-jobs" element={<BackgroundJobsPage />} />
                  <Route path="documentation" element={<DocumentsPage />} />
                  <Route path="settings" element={<SettingPage />} />
                  <Route path="options" element={<OptionsPage />} />
                  <Route path="users" element={<UserPage />} />
                  <Route path="403" element={<Page403 />} />
                  <Route path="404" element={<Page404 />} />
                  <Route
                    path="*"
                    element={
                      <Navigate to={auth?.role === Role.SUPER_ADMIN ? 'clients' : 'settings'} />
                    }
                  />
                </Routes>
              </LayoutAdmin>
            </RoomProvider>
          </RequireAdminAuth>
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/forgot-password/success" element={<ForgotPasswordSuccessPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password/success" element={<ResetPasswordSuccessPage />} />
    </Routes>
  );
}
